import { BaseTeamIdProps } from "@custom-types/base-types";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props {
  /** Open team details page */
  openTeamDetails({ teamId, numberOfMembers }: OpenTeamDetails): void;

  /** Return true if the value is a valid team name */
  isTeamName(value: string): boolean;

  /** Return team Id if any team found by provided name */
  getTeamIdByName(teamName: string): string | undefined;
}

export interface OpenTeamDetails extends BaseTeamIdProps {
  /** The total number of members in a team */
  numberOfMembers?: number;
}

/** Custom hooks to have util functions for team */
export function useTeamUtils(): Props {
  const { trackEvent } = useTrackEvent();
  const { navigateToTeamDetails } = useAppNavigation();
  const companyId = useCompanyId();
  const teams = useAppSelector(teamsSelector);

  function isTeamName(value: string): boolean {
    return teams.some((team) => team.name === value);
  }

  function getTeamIdByName(teamName: string): string | undefined {
    const team = teams.find((team) => team.name === teamName);
    return team?.identity;
  }

  function openTeamDetails({ teamId, numberOfMembers }: OpenTeamDetails): void {
    trackEvent({
      name: TeamEvents.openTeam,
      props: {
        teamId,
        numberOfMembers: numberOfMembers ?? "",
      },
    });

    // Navigate to team details page
    navigateToTeamDetails({
      companyId,
      teamId,
    });
  }

  return { openTeamDetails, isTeamName, getTeamIdByName };
}
